import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

interface GalleryCarouselItem {
  src: string;
  srcSet?: string;
  alt: string;
}

interface GalleryCarouselProps {
  items: GalleryCarouselItem[];
}

/** Horizontally scrollable gallery preview with arrow navigation and a full-screen lightbox. */
export function GalleryCarousel({ items }: GalleryCarouselProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const touchStartX = useRef<number | null>(null);

  const scrollByDir = (dir: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  const open = (i: number) => setActiveIndex(i);
  const close = useCallback(() => setActiveIndex(null), []);
  const step = useCallback(
    (dir: number) =>
      setActiveIndex((cur) =>
        cur === null ? cur : (cur + dir + items.length) % items.length,
      ),
    [items.length],
  );

  useEffect(() => {
    if (activeIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [activeIndex, close, step]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((it, i) => (
          <figure
            key={i}
            className="group relative aspect-[4/3] w-[78%] shrink-0 snap-start overflow-hidden rounded-2xl border border-border bg-card shadow-sm sm:w-[360px]"
          >
            <button
              type="button"
              onClick={() => open(i)}
              aria-label={`Open image: ${it.alt}`}
              className="block h-full w-full cursor-zoom-in"
            >
              <img
                src={it.src}
                srcSet={it.srcSet}
                sizes="(max-width: 640px) 78vw, 360px"
                width={800}
                height={600}
                alt={it.alt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </button>
          </figure>
        ))}
      </div>

      {/* Centered navigation below the preview */}
      <div className="mt-5 flex items-center justify-center gap-3">
        <button
          type="button"
          aria-label="Previous"
          onClick={() => scrollByDir(-1)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-primary shadow-md transition-colors hover:border-accent hover:text-accent"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          aria-label="Next"
          onClick={() => scrollByDir(1)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-primary shadow-md transition-colors hover:border-accent hover:text-accent"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* LIGHTBOX — portaled to body so it fully covers navbar & footer */}
      {activeIndex !== null &&
        createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-4 bg-black p-4"
          onClick={close}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <button
            type="button"
            aria-label="Close preview"
            onClick={close}
            className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <X className="h-6 w-6" />
          </button>

          <figure className="max-h-[78vh] max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={items[activeIndex].src}
              srcSet={items[activeIndex].srcSet}
              sizes="90vw"
              width={1264}
              height={848}
              alt={items[activeIndex].alt}
              className="mx-auto max-h-[72vh] w-auto max-w-full rounded-lg object-contain shadow-2xl"
            />
            <figcaption className="mt-3 text-center text-sm text-white/80">
              {items[activeIndex].alt}
              <span className="ml-2 text-white/50">
                {activeIndex + 1} / {items.length}
              </span>
            </figcaption>
          </figure>

          {/* Centered navigation below the image */}
          <div className="flex items-center justify-center gap-6" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              aria-label="Previous image"
              onClick={(e) => {
                e.stopPropagation();
                step(-1);
              }}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            >
              <ChevronLeft className="h-7 w-7" />
            </button>
            <button
              type="button"
              aria-label="Next image"
              onClick={(e) => {
                e.stopPropagation();
                step(1);
              }}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            >
              <ChevronRight className="h-7 w-7" />
            </button>
          </div>
        </div>,
          document.body,
        )}
    </div>
  );
}

